// A bar label that repeats across a long bar (ZT 738-754, reimplemented).
//
// A five-month timeline bar can be thousands of pixels wide; a single label
// at its left edge is off-screen for most of the scroll. Repeating it every
// ~170px keeps the name under the reader's eye wherever they are. Callers
// estimate the label's width in px (≈6.8/char at this size); the count is
// capped so a pathological width cannot mint thousands of nodes.
//
// A SINGLE label is sticky (`stickyLeft`). The boards scroll under a frozen
// name column and auto-scroll to centre today, so the first visible day can
// sit half under that column, and a bar starting there lost the first letters
// of its job ("ulvert upgrade", found 2026-10-01 making the ads). A sticky
// label rides the visible left edge of its bar instead, staying inside it.
// That only works while NO ancestor between the label and the board's
// scroller is `overflow: hidden`, which makes a scroll container and quietly
// turns sticky off; the bar clips with `.bar-clip` (`overflow: clip`) instead.
import type { ReactNode } from 'react';

/** The inset from the name column's edge to a stuck label's text, px. */
const STUCK_INSET = 8;

export function RepeatingLabel({
  width,
  labelWidth,
  gap = 170,
  stickyLeft,
  children,
}: {
  /** The bar's rendered width, px. */
  width: number;
  /** Estimated label width, px. */
  labelWidth: number;
  gap?: number;
  /** The frozen name column's width, px: where a single label sticks. */
  stickyLeft?: number;
  children: ReactNode;
}) {
  const repeats = Math.min(40, Math.max(1, Math.floor(width / (labelWidth + gap))));

  if (repeats <= 1) {
    return (
      <div className="bar-clip flex w-full items-center px-2">
        <span
          className="truncate"
          style={
            stickyLeft === undefined
              ? undefined
              : { position: 'sticky', left: stickyLeft + STUCK_INSET }
          }
        >
          {children}
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-around overflow-hidden">
      {Array.from({ length: repeats }, (_, i) => (
        <span key={i} className="shrink-0 whitespace-nowrap">
          {children}
        </span>
      ))}
    </div>
  );
}
